import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import api from '../services/api';
import './UnsubscribePage.css';

const UnsubscribePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [email, setEmail] = useState('');
  const [unsubscribed, setUnsubscribed] = useState(false);

  useEffect(() => {
    // Priority order: token > URL email > logged-in user email
    const emailParam = searchParams.get('email');
    const token = searchParams.get('token');

    if (token) {
      // Handle token-based unsubscribe from email
      handleTokenUnsubscribe(token);
      return;
    }

    // Check for email in URL first (from footer link)
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
    // If no URL email, check if user is logged in
    else if (authService.isAuthenticated()) {
      try {
        const user = authService.getCurrentUser();
        if (user?.email) {
          setEmail(user.email);
          console.log('✅ Pre-populated email from logged-in user:', user.email);
        }
      } catch (error) {
        console.error('Error getting user email:', error);
      }
    }
  }, [searchParams]);

  const handleTokenUnsubscribe = async (token) => {
    setProcessing(true);
    try {
      console.log('🔍 Token unsubscribe for token:', token);

      // ✅ Use api instance instead of fetch
      const response = await api.post(`/newsletter/unsubscribe/${token}`);

      console.log('✅ Token unsubscribe response:', response.data);

      setMessage({
        type: 'success',
        text: '✅ You have been successfully unsubscribed from our newsletter.'
      });
      setUnsubscribed(true);
    } catch (error) {
      console.error('❌ Token unsubscribe error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Invalid or expired unsubscribe link.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsubscribe = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      console.log('🔍 Unsubscribe request for:', email);

      // ✅ Use api instance instead of fetch
      const response = await api.post('/newsletter/unsubscribe', {
        email: email.toLowerCase().trim()
      });

      console.log('✅ Unsubscribe response:', response.data);

      setMessage({
        type: 'success',
        text: '✅ You have been successfully unsubscribed from our newsletter.'
      });
      setUnsubscribed(true);
    } catch (error) {
      console.error('❌ Unsubscribe error:', error);
      console.error('❌ Error response:', error.response);

      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.response?.data?.message || 'An error occurred. Please try again or contact admin@focusview.ai'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Success view
  if (unsubscribed) {
    return (
      <div className="unsubscribe-page">
        <div className="unsubscribe-container">
          <div className="unsubscribe-header">
            <h1>✅ Successfully Unsubscribed</h1>
          </div>

          <div className="message success">
            {message.text}
          </div>

          {email && (
            <div className="email-display-box">
              {email}
            </div>
          )}

          <p className="status-text">
            You will no longer receive newsletter emails from us.
          </p>

          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Unsubscribe form
  return (
    <div className="unsubscribe-page">
      <div className="unsubscribe-container">
        <div className="unsubscribe-header">
          <h1>😢 Unsubscribe from Newsletter</h1>
        </div>

        <p className="status-text">
          We're sorry to see you go. {email ? 'Confirm your email to unsubscribe' : 'Enter your email to unsubscribe'} from our newsletter.
        </p>

        <form onSubmit={handleUnsubscribe} className="action-section">
          <input
            type="email"
            className="email-input"
            placeholder="your-email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={processing}
            readOnly={!!email}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e0e0e0',
              borderRadius: '6px',
              fontSize: '16px',
              marginBottom: '15px',
              boxSizing: 'border-box',
              backgroundColor: email ? '#f8f9fa' : 'white',
              cursor: email ? 'not-allowed' : 'text'
            }}
          />

          {message.text && message.type === 'error' && (
            <div className="message error" style={{
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '15px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb'
            }}>
              {message.text}
            </div>
          )}

          <button
            type="submit"
            className="unsubscribe-button"
            disabled={processing || !email}
            style={{
              width: '100%',
              padding: '14px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: processing || !email ? 'not-allowed' : 'pointer',
              backgroundColor: '#dc3545',
              color: 'white',
              marginBottom: '10px',
              opacity: processing || !email ? 0.6 : 1
            }}
          >
            {processing ? 'Processing...' : 'Unsubscribe'}
          </button>

          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/')}
            disabled={processing}
            style={{
              width: '100%',
              padding: '14px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              color: '#333'
            }}
          >
            Cancel
          </button>
        </form>

        <p style={{
          textAlign: 'center',
          color: '#999',
          fontSize: '14px',
          marginTop: '20px'
        }}>
          You can resubscribe anytime by visiting our{' '}
          <a href="/" style={{ color: '#667eea', textDecoration: 'none' }}>
            homepage
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default UnsubscribePage;